import React, { useState } from 'react';
import { useAudio } from '../../contexts/AudioContext';
import { SkipForward, Clock } from 'lucide-react';
import { LiveClock } from './LiveClock';

const DEFAULT_ART = 'https://images.pexels.com/photos/32589034/pexels-photo-32589034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200';

function formatDuration(seconds) {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function UpNext() {
  const { stationData } = useAudio();
  const [imgError, setImgError] = useState(false);
  const nextTrack = stationData?.playing_next;
  const song = nextTrack?.song;
  
  const artUrl = imgError ? DEFAULT_ART : (song?.art || DEFAULT_ART);

  if (!nextTrack || !song) {
    return (
      <div 
        className="glass rounded-2xl p-4"
        data-testid="up-next-panel"
      >
        <div className="flex items-center gap-2 mb-4">
          <SkipForward className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
          <h3 
            className="text-lg font-bold"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Up Next
          </h3>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Loading next track...
        </p>
      </div>
    );
  }

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="up-next-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SkipForward className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
          <h3 
            className="text-lg font-bold"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Up Next
          </h3>
        </div>
        <LiveClock compact />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative group">
          <img
            src={artUrl}
            alt={song.title}
            className="w-20 h-20 rounded-xl object-cover shadow-lg transition-transform group-hover:scale-105"
            onError={() => setImgError(true)}
            data-testid="up-next-art"
          />
          <div 
            className="absolute inset-0 rounded-xl bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <SkipForward className="w-6 h-6 text-white" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p 
            className="font-bold text-lg truncate"
            style={{ 
              fontFamily: 'Teko, sans-serif',
              color: 'var(--text-main)' 
            }}
            data-testid="up-next-title"
          >
            {song.title}
          </p>
          <p 
            className="text-sm truncate"
            style={{ color: 'var(--text-muted)' }}
            data-testid="up-next-artist"
          >
            {song.artist}
          </p>
          
          <div className="flex items-center gap-3 mt-2">
            {nextTrack.playlist && (
              <span 
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ 
                  backgroundColor: 'var(--surface-highlight)',
                  color: 'var(--secondary)'
                }}
              >
                {nextTrack.playlist}
              </span>
            )}
            <span 
              className="flex items-center gap-1 text-xs"
              style={{ color: 'var(--text-muted)' }}
            >
              <Clock className="w-3 h-3" />
              {formatDuration(nextTrack.duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
